// @givmo/app-charity — logo upload + preview (CPA-WEB6).
//
// Pick an image → PREVIEW it locally (object URL) → upload via `charityAdminUploadCharityProfileLogo`
// (multipart POST, `body:{ file }`) → the computed `logo_url` refreshes on the profile. Owner-only:
// the parent renders this only for an Owner (the backend gates the upload on `charity_profile:write`).
//
// The upload is a SINGLE multipart POST (the sdk's `formDataBodySerializer` builds the form-data body) —
// unlike the claim doc flow's 3-step signed-URL PUT. The backend reuses the EIN-keyed public logo path
// and returns `{ logo_url, message }`; we hand the fresh URL up so the parent can reflect it immediately
// and invalidate the profile query.

import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { charityAdminUploadCharityProfileLogoMutation } from '@givmo/api-client/query';
import { isGivmoError, type CharityLogoUploadOut } from '@givmo/api-client';
import { Avatar, Button, Icon, testIdProp, toast } from '@givmo/design-system';
import { ImagePlus, UploadCloud } from 'lucide-react';
import {
  LOGO_ACCEPT,
  LOGO_MAX_BYTES,
  LOGO_TOO_LARGE_MESSAGE,
  LOGO_WRONG_TYPE_MESSAGE,
  isAcceptableLogo,
} from './profile-provenance';

const PREFIX = 'profile-logo';

export interface LogoUploaderProps {
  charityId: string;
  /** The organization name — drives the fallback-initials avatar when there is no logo. */
  orgName: string;
  /** The current computed logo URL (null when none yet). */
  logoUrl: string | null | undefined;
  /** Called with the upload result (fresh `logo_url`) after a successful upload. */
  onUploaded: (result: CharityLogoUploadOut) => void | Promise<unknown>;
}

export function LogoUploader({ charityId, orgName, logoUrl, onUploaded }: LogoUploaderProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  // A locally-previewed pick (object URL) shown BEFORE the upload resolves; cleared once the server URL
  // takes over (or on error). Revoked on unmount / replacement to avoid leaking blobs.
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  // `logo_url` is DERIVED from the EIN, so replacing a logo returns a byte-identical URL: the <img>
  // src never changes, the browser serves the copy it already has, and a successful replace looks
  // like it did nothing. Stamp the moment of a successful upload and hang it off the URL so the
  // fetch that follows is a genuine one. Upload-local — every other surface converges on the
  // object's own Cache-Control instead.
  const [uploadedAt, setUploadedAt] = useState<number | null>(null);

  useEffect(() => {
    // Revoke the previous object URL whenever it changes or the component unmounts.
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const upload = useMutation({
    ...charityAdminUploadCharityProfileLogoMutation(),
    onSuccess: async (result) => {
      setFileError(null);
      setUploadedAt(Date.now());
      await onUploaded(result);
      // Let the server URL take over; drop the local preview.
      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      toast.success(result.message || 'Logo updated.');
    },
    onError: (error) => {
      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      toast.error(
        isGivmoError(error) ? error.message : 'We couldn’t upload your logo. Please try again.',
      );
    },
  });

  function onPick(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = ''; // allow re-picking the same file
    if (!file) return;
    if (!isAcceptableLogo(file)) {
      setFileError(LOGO_WRONG_TYPE_MESSAGE);
      return;
    }
    if (file.size > LOGO_MAX_BYTES) {
      setFileError(LOGO_TOO_LARGE_MESSAGE);
      return;
    }
    setFileError(null);
    // Preview locally, then upload.
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
    upload.mutate({ path: { charity_id: charityId }, body: { file } });
  }

  // The image shown: the in-flight local preview wins, else the server logo (cache-busted once this
  // session has uploaded one), else initials fallback.
  const serverSrc =
    logoUrl && uploadedAt
      ? `${logoUrl}${logoUrl.includes('?') ? '&' : '?'}v=${uploadedAt}`
      : logoUrl;
  const shownSrc = previewUrl ?? serverSrc ?? null;

  return (
    <div className="flex items-center gap-16" {...testIdProp(`${PREFIX}-row`)}>
      <div className="shrink-0" {...testIdProp(`${PREFIX}-preview`)}>
        <Avatar src={shownSrc} name={orgName} size="lg" testId={`${PREFIX}-avatar`} />
      </div>
      <div className="flex min-w-0 flex-col gap-6">
        <div className="flex items-center gap-8">
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            loading={upload.isPending}
            leadingIcon={<Icon icon={logoUrl ? UploadCloud : ImagePlus} size={14} aria-hidden />}
            onClick={() => inputRef.current?.click()}
            testId={`${PREFIX}-button`}
          >
            {logoUrl ? 'Replace logo' : 'Upload logo'}
          </Button>
          <input
            ref={inputRef}
            type="file"
            accept={LOGO_ACCEPT}
            className="hidden"
            onChange={onPick}
            {...testIdProp(`${PREFIX}-input`)}
          />
        </div>
        <p className="text-s12 text-text-tertiary">
          A square PNG, JPG, or WebP works best. Shown to donors on your organization’s page.
        </p>
        {fileError ? (
          <p className="text-s12 text-danger-600" {...testIdProp(`${PREFIX}-file-error`)}>
            {fileError}
          </p>
        ) : null}
      </div>
    </div>
  );
}
